"use client";

import { useEffect, useRef } from "react";
type DispatchDomEventOptions = {
  /**
   * If true (default), prevents double-firing in React 18 Strict Mode dev
   * (mount → unmount → mount) by deduping identical payloads per event name.
   */
  strictModeSafe?: boolean;

  /**
   * If true (default), also fires once on initial mount (when value is non-null).
   */
  fireOnMount?: boolean;
};

/**
 * Dispatches a CustomEvent whenever `value` changes.
 * - Uses deep-ish stable stringify to detect meaningful changes.
 * - Strict Mode safe: prevents duplicate dispatch of the same (eventName + payload) in dev.
 *
 * Usage:
 *   useDispatchEventOnChange("adtargets:ready", { ids: ["a","b"] });
 */
export function useDispatchEventOnChange<T extends Record<string, unknown>>(eventName: string, value: T | null | undefined, options: DispatchDomEventOptions = {}) {
  const {
    strictModeSafe = true,
    fireOnMount = true
  } = options;
  const isFirstRunRef = useRef(true);
  const lastPayloadKeyRef = useRef<string>("");
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!value) return;

    // Global-ish dedupe across StrictMode remounts (dev only) and also across component remounts.
    // Stored on window so it survives the StrictMode intentional remount cycle.
    const strictDedupeMap = (window as WindowWithDedupe).__eventPayloadDedupe__ ??= new Map<string, string>();
    const payloadKey = stableStringify(value);

    // Skip firing on the first run if desired
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      if (!fireOnMount) {
        lastPayloadKeyRef.current = payloadKey;
        return;
      }
    }

    // Only fire if the payload changed (within this component instance)
    if (payloadKey === lastPayloadKeyRef.current) return;

    // Strict-mode safe dedupe: prevent immediate duplicate dispatch of same payload
    if (strictModeSafe) {
      const mapKey = eventName;
      const lastSent = strictDedupeMap.get(mapKey);
      if (lastSent === payloadKey) {
        lastPayloadKeyRef.current = payloadKey;
        return;
      }
      strictDedupeMap.set(mapKey, payloadKey);
    }
    lastPayloadKeyRef.current = payloadKey;
    window.dispatchEvent(new CustomEvent(eventName, {
      detail: value
    }));
  }, [eventName, value, strictModeSafe, fireOnMount]);
}
type WindowWithDedupe = Window & {
  __eventPayloadDedupe__?: Map<string, string>;
};

/** Stable stringify with key sorting so object key order doesn’t cause spurious "changes". */
function stableStringify(input: unknown): string {
  return JSON.stringify(input, (_key, val) => {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;
      return Object.keys(obj).sort().reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = obj[k];
        return acc;
      }, {});
    }
    return val;
  });
}